import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { Button } from "@mui/joy";
import { Box, Paper, Tooltip, Typography } from "@mui/material";
import {
  DataGridPremium as DataGridPro,
  GridActionsCellItem,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Dispatch } from "@reduxjs/toolkit";
import { ConfirmOptions, useConfirm } from "material-ui-confirm";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch } from "react-redux";
import {
  defaultDataGridProps,
  injectDefaultColumnProps,
} from "../../constants";
import { Benefit_Entity } from "../../entities/benefit";
import { JobCategory_Entity } from "../../entities/jobCategory";
import { ProjectType_Entity } from "../../entities/projectType";
import {
  useCreateBenefitMutation,
  useDeleteBenefitMutation,
  useUpdateBenefitMutation,
} from "../../redux/benefit/benefitEndpoints";
import { useBenefits } from "../../redux/benefit/benefitHooks";
import { useJobCategories } from "../../redux/jobCategory/jobCategoryHooks";
import { useProjectTypes } from "../../redux/projectType/projectTypeHooks";
import {
  getterDivider,
  setterDivider,
} from "../jobCategory/jobCategoryRates/dataGridColDef";
import NoRowsOverlay from "../noRowsOverlay/noRowsOverlay";
import { setFormOpen, setSelectedBenefitID } from "../../redux/filmsReducer";

/**
 * Benefit
 *
 * @returns {null} Benefit
 */
export default function Benefits() {
  const { benefits } = useBenefits();
  const { projectTypes } = useProjectTypes();
  const { jobCategories } = useJobCategories();
  const [updateBenefit] = useUpdateBenefitMutation();
  const [deleteBenefit] = useDeleteBenefitMutation();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const confirm = useConfirm();

  return (
    <Paper
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
        maxHeight: "calc(100vh - 78px)",
      }}
    >
      <DataGridPro
        {...defaultDataGridProps}
        apiRef={apiRef}
        onRowClick={(e) => {
          dispatch(setSelectedBenefitID(e.row.id));
          dispatch(setFormOpen({ isOpen: true, formID: "benefit" }));
        }}
        rows={benefits ?? []}
        columns={benefitColumns(
          dispatch,
          confirm,
          projectTypes ?? [],
          jobCategories ?? [],
          deleteBenefit
        )}
        showToolbar
        slots={{
          toolbar: BenefitToolbar,
          noRowsOverlay: NoRowsOverlay,
        }}
        initialState={{
          pinnedColumns: {
            left: ["id"],
            right: ["actions"],
          },
        }}
      />
    </Paper>
  );
}

function BenefitToolbar() {
  const [createBenefit] = useCreateBenefitMutation();

  const handleNewBenefit = () => {
    const newBenefit = {
      id: -1,
      projectTypeID: 0,
      jobCategoryID: 0,
      pension: 0,
      vacation: 0,
      flexDivisor: 0,
      flexMultiplier: 0,
      flexDayHours: 0,
      name: "",
      createdAt: "",
      updatedAt: "",
    };
    createBenefit(newBenefit);
  };

  useHotkeys(`ctrl+enter`, handleNewBenefit, {
    enableOnTags: ["INPUT", "SELECT", "TEXTAREA"],
  });

  return (
    <Toolbar>
      <Box
        sx={{
          display: "flex",
          p: 1,
          alignItems: "center",
          gap: 2,
          width: "100%",
        }}
      >
        <Tooltip title="ctrl + enter">
          <Button size="sm" variant="soft" onClick={handleNewBenefit}>
            + New Benefit
          </Button>
        </Tooltip>
        <QuickFilter
          expanded
          style={{ flex: 1, display: "flex", alignItems: "center" }}
        >
          <QuickFilterControl size="small" fullWidth placeholder="Search..." />
          <QuickFilterClear />
        </QuickFilter>
      </Box>
    </Toolbar>
  );
}

const benefitColumns = (
  dispatch: Dispatch,
  confirm: (options?: ConfirmOptions | undefined) => Promise<void>,
  projectTypes: ProjectType_Entity[],
  jobCategories: JobCategory_Entity[],
  deleteBenefit: (id: number) => void
) =>
  injectDefaultColumnProps([
    {
      field: "id",
      headerName: "ID",
      editable: false,
      type: "number",
      width: 40,
    },
    {
      field: "projectTypeID",
      headerName: "Project Type",
      editable: true,
      width: 200,
      type: "singleSelect",
      valueOptions: getOptions(projectTypes),
      renderCell: (p: any) => {
        const projectType = projectTypes.find(
          (s) => s.id === p.row.projectTypeID
        );
        return <Typography variant="body2">{projectType?.name}</Typography>;
      },
    },
    {
      field: "jobCategoryID",
      headerName: "Job Category",
      editable: true,
      width: 200,
      type: "singleSelect",
      valueOptions: getOptions(jobCategories),
      renderCell: (p: any) => {
        const jobCategory = jobCategories.find(
          (s) => s.id === p.row.jobCategoryID
        );
        return <Typography variant="body2">{jobCategory?.name}</Typography>;
      },
    },
    {
      field: "pension",
      headerName: "Pension",
      editable: true,
      type: "number",
      width: 150,
      valueGetter: getterDivider("pension", 10000),
      valueSetter: setterDivider("pension", 10000),
    },
    {
      field: "vacation",
      headerName: "Vacation",
      editable: true,
      type: "number",
      width: 150,
      valueGetter: getterDivider("vacation", 10000),
      valueSetter: setterDivider("vacation", 10000),
      valueFormatter: (valueOrParams: any) =>
        `${valueOrParams?.value ?? valueOrParams}%`,
    },
    {
      field: "welfare",
      headerName: "Welfare",
      editable: true,
      type: "number",
      width: 150,
      valueGetter: getterDivider("welfare", 10000),
      valueSetter: setterDivider("welfare", 10000),
      valueFormatter: (valueOrParams: any) =>
        `${valueOrParams?.value ?? valueOrParams}%`,
    },
    {
      field: "flexDivisor",
      headerName: "Flex Divisor",
      editable: true,
      type: "number",
      width: 150,
      valueGetter: getterDivider("flexDivisor", 10000),
      valueSetter: setterDivider("flexDivisor", 10000),
    },
    {
      field: "flexMultiplier",
      headerName: "Flex Multiplier",
      editable: true,
      type: "number",
      width: 150,
      valueGetter: getterDivider("flexMultiplier", 10000),
      valueSetter: setterDivider("flexMultiplier", 10000),
    },
    {
      field: "flexDayHours",
      headerName: "Flex Day Hours",
      editable: true,
      type: "number",
      width: 150,
      valueGetter: getterDivider("flexDayHours", 100),
      valueSetter: setterDivider("flexDayHours", 100),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: (props: { id: any }) => [
        <GridActionsCellItem
          key="delete"
          showInMenu
          icon={<DeleteIcon />}
          label={`Delete `}
          onClick={() => {
            confirm({
              description: `You are about to delete a benefit`,
            })
              .then(() => {
                deleteBenefit(props.id);
              })
              .catch(() => {
                /* ... */
              });
          }}
          color="inherit"
        />,
      ],
    },
  ]);

function getOptions(items: { id: number; name?: string }[]) {
  const res = [];
  for (const key in items) {
    if (Object.prototype.hasOwnProperty.call(items, key)) {
      const item = items[key];
      res.push({
        value: item.id,
        label: `${item.id}: ${item.name}`,
      });
    }
  }

  return res;
}
